import {
  Activity,
  BookOpen,
  Brain,
  ChevronLeft,
  ClipboardCheck,
  DollarSign,
  FileText,
  FolderOpen,
  Home,
  Inbox,
  LayoutGrid,
  Mail,
  MessageSquare,
  PinIcon,
  Printer,
  Receipt,
  Scale,
  Send,
  Shield,
  Stethoscope,
} from 'lucide-react';
import React, { useCallback, useMemo, useState } from 'react';

import {
  Sidebar,
  SidebarBody,
  SidebarFooter,
  SidebarItem,
  SidebarLabel,
  SidebarSection,
} from '@/components/ui/catalyst/sidebar';
import { useIsV3 } from '@/hooks/useAppVersion';
import { cn } from '@/lib/utils';
import {
  useGetUnreadClientCommsCountQuery,
  useMarkCommunicationsViewedMutation,
} from '@/services/api/clientCommunicationsService';

interface NavItem {
  id: string;
  label: string;
  icon: React.ElementType;
  shortcut?: string;
  disabled?: boolean;
  badge?: number;
  dividerAfter?: boolean;
}

interface MatterSideNavProps {
  matterId: string;
  activeTab: string;
  onTabChange: (tab: string) => void;
  isLoading?: boolean;
  isPinned: boolean;
  onTogglePin: () => void;
  className?: string;
}

const navItems: NavItem[] = [
  // Overview
  {
    id: 'home',
    label: 'Overview',
    icon: Home,
    shortcut: '1',
    dividerAfter: true,
  },
  // Treatment, coverage, police, demand, workspaces
  { id: 'treatment', label: 'Treatment', icon: Stethoscope, shortcut: '2' },
  { id: 'coverage', label: 'Coverage', icon: Shield, shortcut: '3' },
  { id: 'risk', label: 'Risk', icon: Brain },
  { id: 'police', label: 'Police', icon: Scale, shortcut: '4' },
  { id: 'demand', label: 'Demand', icon: FileText, shortcut: '5' },
  { id: 'document-review', label: 'Document Review', icon: ClipboardCheck },
  {
    id: 'workspaces',
    label: 'Workspaces',
    icon: MessageSquare,
    shortcut: '6',
    dividerAfter: true,
  },
  { id: 'wiki', label: 'Wiki', icon: BookOpen },
  // Client Comms, Correspondence, mail, fax, ledger
  { id: 'client-comms', label: 'Client Comms', icon: Inbox, shortcut: '7' },
  { id: 'correspondence', label: 'Correspondence', icon: Send, shortcut: '8' },
  { id: 'mail', label: 'Mail', icon: Mail, shortcut: '9' },
  { id: 'fax', label: 'Fax', icon: Printer },
  { id: 'ledger', label: 'Ledger', icon: DollarSign },
  {
    id: 'case-expenses',
    label: 'Case Expenses',
    icon: Receipt,
    dividerAfter: true,
  },
  // Documents, Document Audit, Artifacts
  { id: 'documents', label: 'Documents', icon: FolderOpen, shortcut: '0' },
  { id: 'deliverables', label: 'Document Audit', icon: LayoutGrid },
  { id: 'artifacts', label: 'Artifacts', icon: Inbox, dividerAfter: true },
  // CMS Sync, Updates
  // { id: 'cms-sync', label: 'CMS Sync', icon: RefreshCw },
  { id: 'updates', label: 'Updates', icon: Activity },
];

export const MatterSideNav: React.FC<MatterSideNavProps> = ({
  matterId,
  activeTab,
  onTabChange,
  isLoading,
  isPinned,
  onTogglePin,
  className,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const isBeta = useIsV3();
  // Only check unreads for the current matter (more efficient than checking all matters)
  const { data: unreadData } = useGetUnreadClientCommsCountQuery(
    { matterId: matterId || '' },
    {
      refetchOnFocus: true,
      pollingInterval: 5000, // Poll every 5s for new messages
      skip: !matterId,
      skipPollingIfUnfocused: true,
    },
  );
  const [markCommunicationsViewed] = useMarkCommunicationsViewedMutation();

  // Handle tab change with auto-mark-viewed for Client Comms
  const handleTabChange = useCallback(
    (tabId: string) => {
      // Mark communications as viewed when navigating to Client Comms tab
      if (tabId === 'client-comms' && matterId) {
        markCommunicationsViewed({ matterId });
      }
      onTabChange(tabId);
    },
    [matterId, markCommunicationsViewed, onTabChange],
  );

  // Check if current matter has unread communications
  // When matterId is passed, the API returns has_unreads boolean directly
  const matterHasUnreads = useMemo(() => {
    if (!unreadData || !matterId) return false;
    return unreadData.has_unreads === true;
  }, [unreadData, matterId]);

  // Filter nav items based on feature flags and add unread badge
  const filteredNavItems = useMemo(() => {
    return navItems
      .filter((item) => {
        // Keep /risk route accessible, but hide the side-nav entry outside beta.
        if (item.id === 'risk' && !isBeta) return false;
        // Wiki is accessible via URL but hidden from the sidebar.
        if (item.id === 'wiki') return false;
        return true;
      })
      .map((item) => {
        // Add unread badge to client-comms if this matter has unreads
        if (item.id === 'client-comms' && matterHasUnreads) {
          return { ...item, badge: 1 }; // Show dot indicator (actual count not needed, just presence)
        }
        return item;
      });
  }, [isBeta, matterHasUnreads]);

  // Handle keyboard shortcuts
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.altKey && !e.ctrlKey && !e.metaKey) {
        const { key } = e;
        const item = navItems.find((t) => t.shortcut === key);
        if (item && !item.disabled) {
          handleTabChange(item.id);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleTabChange]);

  // Show expanded labels on hover OR when pinned
  const showExpanded = isHovered || isPinned;

  return (
    <div
      className={cn('relative h-full flex-shrink-0', className)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Spacer div - takes up actual layout space */}
      <div
        className={cn(
          'h-full transition-all duration-200 ease-in-out',
          isPinned ? 'w-40' : 'w-12',
        )}
      />

      {/* Sidebar - absolute positioned, overlays on hover when not pinned */}
      <div
        className={cn(
          'absolute bg-white transition-all duration-200 ease-in-out z-20',
          showExpanded ? 'w-40' : 'w-12',
          isPinned
            ? 'top-0 left-0 h-full border-r border-zinc-200 shadow-[2px_0_8px_-2px_rgba(0,0,0,0.08)]'
            : 'top-3 left-2 h-[calc(100%-1.5rem)] rounded-xl border border-zinc-200 shadow-lg',
        )}
      >
        <Sidebar className="h-full">
          <SidebarBody className="py-2 px-1.5">
            <SidebarSection className="gap-0.5">
              {filteredNavItems.map((item) => {
                const Icon = item.icon;
                const isActive = activeTab === item.id;

                return (
                  <React.Fragment key={item.id}>
                    <SidebarItem
                      onClick={() =>
                        !item.disabled && !isLoading && handleTabChange(item.id)
                      }
                      current={isActive}
                      className={cn(
                        'relative cursor-pointer transition-colors duration-150 rounded-lg',
                        item.disabled && 'opacity-50 cursor-not-allowed',
                        isActive && 'bg-primary/10 text-primary',
                        !isActive && 'hover:bg-zinc-100 hover:rounded-lg',
                      )}
                    >
                      <Icon
                        className={cn(
                          'h-4 w-4 flex-shrink-0 fill-none',
                          isActive ? 'text-primary' : 'text-zinc-500',
                        )}
                      />
                      {showExpanded && (
                        <SidebarLabel
                          className={cn(
                            'text-xs whitespace-nowrap',
                            isActive
                              ? 'text-primary font-medium'
                              : 'text-zinc-600',
                          )}
                        >
                          {item.label}
                        </SidebarLabel>
                      )}
                      {item.badge !== undefined && item.badge > 0 && (
                        <span
                          className={cn(
                            'absolute flex items-center justify-center rounded-full bg-primary text-white text-xs font-medium',
                            showExpanded
                              ? 'right-2 top-1/2 -translate-y-1/2 h-5 min-w-5 px-1'
                              : 'top-0 right-0 h-4 min-w-4 px-0.5 text-[10px]',
                          )}
                        >
                          {item.badge}
                        </span>
                      )}
                    </SidebarItem>
                    {item.dividerAfter && (
                      <div
                        className={cn(
                          'border-t border-zinc-200 transition-all duration-200 ease-in-out',
                          isPinned ? 'my-2 mx-3' : 'my-0.5 mx-1',
                        )}
                      />
                    )}
                  </React.Fragment>
                );
              })}
            </SidebarSection>
          </SidebarBody>

          <SidebarFooter className="px-1.5 py-1.5 border-t border-zinc-200">
            <SidebarItem
              onClick={onTogglePin}
              className="cursor-pointer hover:bg-zinc-100 transition-colors duration-150 rounded-lg"
            >
              {isPinned ? (
                <ChevronLeft className="h-4 w-4 text-zinc-500 fill-none" />
              ) : (
                <PinIcon className="h-4 w-4 text-zinc-500 fill-none" />
              )}
              {showExpanded && (
                <SidebarLabel className="text-xs text-zinc-600">
                  {isPinned ? 'Collapse' : 'Pin'}
                </SidebarLabel>
              )}
            </SidebarItem>
          </SidebarFooter>
        </Sidebar>
      </div>
    </div>
  );
};

export default MatterSideNav;
